import { useEffect, useRef, useState } from 'react';
import { useLocation } from '@react-router';
import { useHistory } from '@shared/core';
import { usePeopleContext } from '@shared/context/people';

const PARAM = 'personIds';

// `location.search` includes the leading "?". Strip it before parsing — URLSearchParams drops a
// leading "?" in browsers but not in jsdom, and the repo already follows this convention (see
// updateSearchParam.ts). Keeps parse + strip correct across browser, node, and the jsdom test env.
const stripLeadingQuestionMark = (search: string): string => search.replace(/^\?/, '');

const parsePersonIds = (search: string): string[] => {
  const raw = new URLSearchParams(stripLeadingQuestionMark(search)).get(PARAM);
  if (!raw) return [];
  return Array.from(
    new Set(
      raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
    ),
  );
};

/**
 * Reads a `?personIds=id1,id2,id3` query param from the URL and seeds
 * PeopleContext with sparse `{ id }` entries. The existing PeoplePickerButton
 * controller auto-hydrates them into full PersonFragment objects once the
 * picker data loads.
 *
 * Strips the param from the URL after applying so a refresh doesn't re-seed.
 * Runs exactly once per mount (ref-guarded).
 *
 * Returns whether the URL carried personIds. Callers (e.g. RsvpReminderProvider)
 * use this to skip their own default pre-selection so it doesn't overwrite the
 * URL-seeded recipients.
 *
 * `enabled` (default true) gates only the *seeding* — the param is always stripped
 * from the URL regardless, so unsupported flows don't leave raw guest IDs lingering
 * in the address bar/history for the whole compose session. The general-message flow
 * passes `!isGeneralEvent`: sparse `{ id }` entries only resolve in the PeoplePicker
 * (person-id keyed) used for wedding/non-general events. General events use the
 * ContactPicker (guest-key keyed), where unmatched ids would inflate the selection
 * count without selecting anyone, so seeding is skipped there — but the param is
 * still consumed/scrubbed.
 *
 * Must be called inside a <PeopleProvider>.
 */
export const usePersonIdsFromQueryParams = (enabled = true): boolean => {
  const location = useLocation();
  const history = useHistory();
  const { setSelectedPeople } = usePeopleContext();
  const appliedRef = useRef(false);
  // Computed synchronously at mount from the URL so callers see the correct value on the very
  // FIRST render — the seeding effect below only performs the side effects, it does not drive this
  // return value. Returning it from the effect (via a ref or a set-in-effect state) would land too
  // late: when the consumer's data query is served from a warm Apollo cache, the consumer's own
  // data-loaded effect fires in the same passive-effect flush as the seeding effect and would
  // overwrite the URL-seeded selection before any re-render could propagate the flag.
  const [hadPersonIds] = useState(() => enabled && parsePersonIds(location.search).length > 0);

  useEffect(() => {
    if (appliedRef.current) return;
    const ids = parsePersonIds(location.search);
    if (ids.length === 0) return;

    appliedRef.current = true;

    // Seed only on flows that can resolve sparse { id } entries (see `enabled`).
    // The picker controller hydrates these into full PersonFragment objects.
    if (enabled) {
      setSelectedPeople(ids.map(id => ({ id })));
    }

    // Always strip the consumed param — even when seeding is disabled — so raw guest IDs
    // don't linger in the URL/history for the rest of the compose session.
    const params = new URLSearchParams(stripLeadingQuestionMark(location.search));
    params.delete(PARAM);
    const nextSearch = params.toString();
    history.replace({
      ...location,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  }, [enabled, history, location, setSelectedPeople]);

  return hadPersonIds;
};
